import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import {
  ArrowUpRight,
  BookHeart,
  BookOpen,
  GraduationCap,
  Home as HomeIcon,
  Languages,
  LoaderCircle,
  Menu,
  Radio,
  Repeat,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  X,
} from "lucide-react";
import { Home } from "./components/Home";
import { configureAuth, fetchAppUser, loadCloudJourney, loadPublicConfig, saveCloudJourney } from "./lib/auth";
import { logPractice, mergeProgress, readLocalProgress, stampActivity, writeLocalProgress } from "./lib/progress";
import { inviteFromLocation } from "./lib/classes";
import { withBase } from "./lib/base";
import type { AppSection, AuthUser, LearnerProgress } from "./types";
import type { HifzDraft, PortionTarget } from "./components/Hifz";

// Each section's code loads when it is first opened. When that fails — a
// dropped connection, or a tab left open across a deploy (the old file is gone
// and the server answers with the page instead) — it is tried once more, then
// the page reloads once to pick up the current files. Only if that fails too
// does SectionBoundary show a message; before, the whole screen went blank.
const RELOADED_KEY = "al-noor-quran-reloaded-at";
function reloadOnce(): boolean {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOADED_KEY) || 0) < 60_000) return false;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
    // Without somewhere to remember the reload, it could loop; better a message.
    if (!sessionStorage.getItem(RELOADED_KEY)) return false;
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}
// (ComponentType<any> is the bound React.lazy itself uses.)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function section<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy<T>(async () => {
    try {
      return await load();
    } catch {
      await new Promise((done) => setTimeout(done, 800));
      try {
        return await load();
      } catch (reason) {
        if (reloadOnce()) return new Promise<never>(() => undefined); // the page is going away
        throw reason;
      }
    }
  });
}
const Qaida = section(() => import("./components/Qaida").then((module) => ({ default: module.Qaida })));
const Quran = section(() => import("./components/Quran").then((module) => ({ default: module.Quran })));
const Journey = section(() => import("./components/Journey").then((module) => ({ default: module.Journey })));
const Hifz = section(() => import("./components/Hifz").then((module) => ({ default: module.Hifz })));
const Classroom = section(() => import("./components/Classroom").then((module) => ({ default: module.Classroom })));
const Resources = section(() => import("./components/Resources").then((module) => ({ default: module.Resources })));
const QaidaAdmin = section(() => import("./components/QaidaAdmin").then((module) => ({ default: module.QaidaAdmin })));

/** What a section shows if it could not load or crashed — instead of a blank screen. */
class SectionBoundary extends Component<{ children: ReactNode; urdu: boolean }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return <div className="notice error section-error" role="alert">
      <span>{this.props.urdu
        ? "یہ حصہ لوڈ نہیں ہو سکا — عموماً انٹرنیٹ کا رابطہ ٹوٹنے سے۔ صفحہ دوبارہ لوڈ کریں۔"
        : "This part of the app did not load — usually a dropped connection. Reload the page to try again."}</span>
      <button className="button primary" onClick={() => window.location.reload()}>{this.props.urdu ? "دوبارہ لوڈ کریں" : "Reload"}</button>
    </div>;
  }
}

// The Hifz portion being worked on survives a reload of the reader (including
// the one above), so the reader still opens on the chosen ayahs.
const PORTION_KEY = "al-noor-quran-portion";
const HIFZ_KINDS = ["sabaq", "sabqi", "manzil"];
function rememberPortion(target: PortionTarget & { nonce: number }) {
  try { sessionStorage.setItem(PORTION_KEY, JSON.stringify(target)); } catch { /* not remembered */ }
}
function forgetPortion() {
  try { sessionStorage.removeItem(PORTION_KEY); } catch { /* nothing to forget */ }
}
function rememberedPortion(): (PortionTarget & { nonce: number }) | null {
  if (window.location.hash !== "#quran") return null;
  try {
    const value = JSON.parse(sessionStorage.getItem(PORTION_KEY) ?? "null") as (PortionTarget & { nonce: number }) | null;
    const valid = value && HIFZ_KINDS.includes(value.kind)
      && [value.from?.surah, value.from?.ayah, value.to?.surah, value.to?.ayah, value.each, value.rounds, value.nonce].every(Number.isInteger)
      && value.from.surah >= 1 && value.to.surah <= 114 && value.from.surah <= value.to.surah;
    return valid ? value : null;
  } catch {
    return null;
  }
}

const LMS_URL = "https://www.alnoortuition.com/lms";
const SECTIONS: AppSection[] = ["home", "qaida", "quran", "hifz", "journey", "classroom", "resources", "admin"];
const NAV: Array<{ id: AppSection; label: string; urdu: string; icon: typeof HomeIcon; management?: true }> = [
  { id: "home", label: "Home", urdu: "ہوم", icon: HomeIcon },
  { id: "qaida", label: "Noorani Qaida", urdu: "نورانی قاعدہ", icon: GraduationCap },
  { id: "quran", label: "Quran", urdu: "قرآن", icon: BookOpen },
  { id: "hifz", label: "Hifz", urdu: "حفظ", icon: Repeat },
  { id: "classroom", label: "Live class", urdu: "براہِ راست کلاس", icon: Radio },
  { id: "resources", label: "Library", urdu: "کتب خانہ", icon: BookHeart },
  { id: "journey", label: "My journey", urdu: "میرا سفر", icon: UserRound },
  { id: "admin", label: "Content", urdu: "مواد", icon: SlidersHorizontal, management: true },
];

function sectionFromHash(): AppSection {
  const value = window.location.hash.replace("#", "") as AppSection;
  if (SECTIONS.includes(value)) return value;
  // A shared class link carries the code as ?room=, and a class invitation as
  // ?invite= — both are instructions to open the classroom, whatever the hash says.
  const params = new URLSearchParams(window.location.search);
  return params.has("room") || params.has("invite") ? "classroom" : "home";
}

function PageLoader() {
  return <div className="section-loader" role="status"><span className="loader-orbit"><LoaderCircle /></span><p>Preparing your learning space…</p></div>;
}

export default function App() {
  const [section, setSection] = useState<AppSection>(sectionFromHash);
  const [mobileNav, setMobileNav] = useState(false);
  // On a phone the bar steps out of the way while reading down the page, and
  // comes back on the way up (and always near the top); an open menu closes as
  // the reader moves on.
  const [barHidden, setBarHidden] = useState(false);
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 840px)");
    let last = window.scrollY;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        const delta = y - last;
        if (Math.abs(delta) < 6) return;
        last = y;
        if (delta > 0) setMobileNav(false);
        setBarHidden(phone.matches && delta > 0 && y > 90);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); window.cancelAnimationFrame(frame); };
  }, []);
  const [progress, setProgress] = useState<LearnerProgress>(() => readLocalProgress());
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [language, setLanguage] = useState<"en" | "ur">(() => localStorage.getItem("al-noor-language") === "ur" ? "ur" : "en");
  const cloudReady = useRef(false);

  useEffect(() => {
    const onHashChange = () => setSection(sectionFromHash());
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("popstate", onHashChange);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("popstate", onHashChange);
    };
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    loadPublicConfig().then(async (config) => {
      setAuthEnabled(config.authEnabled);
      const client = configureAuth(config);
      if (!client) return;
      const { data } = await client.auth.getSession();
      setSession(data.session);
      const listener = client.auth.onAuthStateChange((_event, next) => setSession(next));
      unsubscribe = () => listener.data.subscription.unsubscribe();
    });
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    if (!session) { setUser(null); cloudReady.current = false; return; }
    setSyncing(true);
    Promise.all([fetchAppUser(session), loadCloudJourney(session)]).then(([nextUser, remote]) => {
      setUser(nextUser);
      if (remote) setProgress((local) => mergeProgress(local, remote));
      cloudReady.current = true;
    }).finally(() => setSyncing(false));
  }, [session?.access_token]);

  useEffect(() => {
    writeLocalProgress(progress);
    if (!session || !cloudReady.current) return;
    const timer = window.setTimeout(() => {
      setSyncing(true);
      saveCloudJourney(session, progress).catch(console.error).finally(() => setSyncing(false));
    }, 800);
    return () => window.clearTimeout(timer);
  }, [progress, session?.access_token]);

  useEffect(() => { localStorage.setItem("al-noor-language", language); }, [language]);

  const update = (recipe: (current: LearnerProgress) => LearnerProgress) => setProgress((current) => recipe(current));
  // Practice — marking a Qaida stage or an ayah, attending a class — is what a
  // streak is made of. Opening the reader is not: it moves "last read" on its
  // own, and counting that would hand out streaks for looking.
  const practise = (recipe: (current: LearnerProgress) => LearnerProgress) => update((current) => stampActivity(recipe(current)));
  const toggleQaida = (id: string) => practise((current) => {
    const exists = current.completedQaida.includes(id);
    return { ...current, completedQaida: exists ? current.completedQaida.filter((item) => item !== id) : [...current.completedQaida, id] };
  });
  const toggleAyah = (key: string) => practise((current) => {
    const exists = current.completedAyahs.includes(key);
    return {
      ...current,
      completedAyahs: exists ? current.completedAyahs.filter((item) => item !== key) : [...current.completedAyahs, key],
      practiceLog: logPractice(current.practiceLog, exists ? -1 : 1),
    };
  });
  const toggleBookmark = (key: string) => update((current) => ({ ...current, bookmarks: current.bookmarks.includes(key) ? current.bookmarks.filter((item) => item !== key) : [...current.bookmarks, key], lastActive: new Date().toISOString() }));
  const setLastRead = (surah: number, ayah: number) => update((current) => current.lastRead?.surah === surah && current.lastRead.ayah === ayah ? current : { ...current, lastRead: { surah, ayah }, lastActive: new Date().toISOString() });
  const navigate = (next: AppSection) => {
    // A portion chosen on the Hifz page applies once; the nav opens the reader plainly.
    setQuranTarget(null);
    forgetPortion();
    setHifzDraft(null);
    setQuranFocus(null);
    setSection(next);
    setMobileNav(false);
    window.history.pushState(null, "", `#${next}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  // The classroom asks the shell to get out of the way: a lesson is the whole
  // screen, and a nav bar advertising six other places to be is not what a child
  // needs while their teacher is talking.
  const [chrome, setChrome] = useState({ inRoom: false, immersive: false });
  const [joinRoom, setJoinRoom] = useState<{ code: string; observe: boolean; autoJoin: boolean } | null>(() => {
    // A class link — /alquran/?room=noor-maryam — opens the lobby with the code
    // already filled in. Deliberately NOT an auto-join: the person arriving has
    // to say who they are, and a link that dropped a stranger into a children's
    // class the moment it was opened would be the wrong thing to build.
    const code = new URLSearchParams(window.location.search).get("room");
    return code ? { code: code.replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 64), observe: false, autoJoin: false } : null;
  });
  const onRoomChange = useCallback((next: { inRoom: boolean; immersive: boolean }) => setChrome(next), []);
  // A portion to open in the reader, ready to loop: set from the Hifz page.
  const [quranTarget, setQuranTarget] = useState<(PortionTarget & { nonce: number }) | null>(rememberedPortion);
  const openPortion = useCallback((portion: PortionTarget) => {
    const target = { ...portion, nonce: Date.now() };
    rememberPortion(target);
    setQuranTarget(target);
    setSection("quran");
    window.history.pushState(null, "", "#quran");
    window.scrollTo({ top: 0 });
  }, []);
  // A reported word, opened from the admin's list: the reader shows its card.
  const [quranFocus, setQuranFocus] = useState<{ surah: number; ayah: number; word: number; nonce: number } | null>(null);
  const openWord = useCallback((surah: number, ayah: number, word: number) => {
    setQuranTarget(null);
    forgetPortion();
    setQuranFocus({ surah, ayah, word, nonce: Date.now() });
    setSection("quran");
    window.history.pushState(null, "", "#quran");
    window.scrollTo({ top: 0 });
  }, []);
  // …and back again: the reader's "Record it" fills in the Hifz form.
  const [hifzDraft, setHifzDraft] = useState<HifzDraft | null>(null);
  const recordPortion = useCallback((portion: Omit<HifzDraft, "nonce">) => {
    setHifzDraft({ ...portion, nonce: Date.now() });
    setSection("hifz");
    window.history.pushState(null, "", "#hifz");
    window.scrollTo({ top: 0 });
  }, []);
  // A class invitation from the address bar. Used once, then removed from the
  // URL so a reload or a bookmark does not try to join again.
  const [invite, setInvite] = useState<string | null>(() => inviteFromLocation(window.location.search));
  const onInviteDone = useCallback(() => {
    setInvite(null);
    window.history.replaceState(null, "", `${withBase("/")}#classroom`);
  }, []);

  /** Record what a live class covered against this learner's own progress. */
  const onCovered = useCallback((coverage: { stageType: string; stageId: string; ayah: number }) => {
    const key = coverage.stageType === "quran"
      ? `quran:${coverage.stageId}:${coverage.ayah}`
      : `${coverage.stageType}:${coverage.stageId}`;
    setProgress((current) => {
      if (current.classCovered.includes(key)) return current;
      return stampActivity({
        ...current,
        classCovered: [...current.classCovered, key],
        // Reading an ayah together in class is exactly what "where I am up to"
        // means, so the Quran reader reopens there afterwards.
        lastRead: coverage.stageType === "quran"
          ? { surah: Number(coverage.stageId) || 1, ayah: coverage.ayah }
          : current.lastRead,
      });
    });
  }, []);

  const onShared = useCallback((resources: Array<{ key: string; title: string; sharedAt: string }>) => {
    setProgress((current) => {
      const known = new Set(current.sharedResources.map((item) => item.key));
      const added = resources.filter((item) => !known.has(item.key));
      if (!added.length) return current;
      return { ...current, sharedResources: [...added, ...current.sharedResources].slice(0, 200) };
    });
  }, []);

  const observeClass = useCallback((code: string) => {
    setJoinRoom({ code, observe: true, autoJoin: true });
    setSection("classroom");
    window.history.pushState(null, "", "#classroom");
  }, []);

  const displayName = user?.name?.split(" ")[0] || "learner";
  // Admin and manager are equivalent here, as they are in the LMS.
  const isManagement = user?.role === "admin" || user?.role === "manager";

  return <div className={`app-shell ${chrome.inRoom ? "in-class" : ""} ${chrome.immersive ? "immersive" : ""}`}>
    <a className="skip-link" href="#main-content">Skip to learning content</a>
    <div className="ambient ambient-one" aria-hidden="true" />
    <div className="ambient ambient-two" aria-hidden="true" />
    <header className={`topbar ${barHidden ? "bar-hidden" : ""}`}>
      <button className="brand" onClick={() => navigate("home")} aria-label="Al Noor Quran home">
        <span className="brand-mark"><img src={withBase("/brand/al-noor-mark.png")} alt="" width={44} height={44} /></span>
        <span><b>Al Noor Quran</b><small>نور القرآن</small></span>
      </button>
      <nav className={mobileNav ? "open" : ""} aria-label="Main navigation">
        {NAV.filter((item) => !item.management || isManagement).map((item) => {
          const Icon = item.icon;
          return <button key={item.id} className={section === item.id ? "active" : ""} onClick={() => navigate(item.id)} aria-current={section === item.id ? "page" : undefined}>
            <Icon /><span>{language === "ur" ? item.urdu : item.label}</span>{item.id === "classroom" ? <i className="nav-live" /> : null}
          </button>;
        })}
        <a className="mobile-lms-link" href={LMS_URL}><ShieldCheck /> LMS account <ArrowUpRight /></a>
      </nav>
      <div className="topbar-actions">
        <div className="language-switch" aria-label="Display language"><Languages /><button className={language === "en" ? "active" : ""} onClick={() => setLanguage("en")}>EN</button><button className={language === "ur" ? "active urdu" : "urdu"} onClick={() => setLanguage("ur")}>اردو</button></div>
        <a className="lms-link" href={LMS_URL}>LMS <ArrowUpRight /></a>
        <button className="profile-chip" onClick={() => navigate("journey")}><span className="avatar">{(user?.name || "Guest").slice(0, 2).toUpperCase()}</span><span><b>{user?.name || "Guest learner"}</b><small>{session ? user?.role || "connected" : "Device journey"}</small></span></button>
        <button className="menu-button" onClick={() => setMobileNav(!mobileNav)} aria-expanded={mobileNav} aria-label="Toggle navigation">{mobileNav ? <X /> : <Menu />}</button>
      </div>
    </header>
    <div className="topbar-spacer" />
    <main className="content-wrap" id="main-content">
      <SectionBoundary key={section} urdu={language === "ur"}>
      <Suspense fallback={<PageLoader />}>
        {section === "home" && <Home progress={progress} navigate={navigate} name={displayName} language={language} />}
        {section === "qaida" && <Qaida progress={progress} toggleComplete={toggleQaida} language={language} user={user} />}
        {section === "quran" && <Quran progress={progress} toggleAyah={toggleAyah} toggleBookmark={toggleBookmark} setLastRead={setLastRead} appLanguage={language} target={quranTarget}
          onRecord={session ? recordPortion : undefined} onBackToHifz={() => navigate("hifz")}
          session={session} canCorrect={isManagement} focus={quranFocus}
          onClosePortion={() => { forgetPortion(); setQuranTarget(null); }} />}
        {section === "hifz" && <Hifz session={session} user={user} language={language}
          plan={progress.hifzPlan} onPlanChange={(hifzPlan) => update((current) => ({ ...current, hifzPlan }))}
          onOpenPortion={openPortion} draft={hifzDraft} />}
        {section === "journey" && <Journey progress={progress} setWeeklyGoal={(weeklyGoal) => update((current) => ({ ...current, weeklyGoal }))} session={session} user={user} authEnabled={authEnabled} syncing={syncing} />}
        {section === "classroom" && <Classroom
          defaultName={user?.name || ""}
          language={language}
          session={session}
          user={user}
          onRoomChange={onRoomChange}
          onCovered={onCovered}
          onShared={onShared}
          joinRoom={joinRoom}
          invite={invite}
          onInviteDone={onInviteDone}
          myHifzPlan={progress.hifzPlan}
          canCorrect={isManagement}
        />}
        {section === "resources" && <Resources language={language} user={user} session={session} progress={progress} />}
        {section === "admin" && <QaidaAdmin session={session} user={user} language={language} onObserve={observeClass} onOpenWord={openWord} />}
      </Suspense>
      </SectionBoundary>
    </main>
    <footer className="site-footer">
      <div className="brand compact"><span className="brand-mark"><img src={withBase("/brand/al-noor-mark.png")} alt="" width={34} height={34} /></span><span><b>Al Noor Quran</b><small>Teacher-led. Learner-owned.</small></span></div>
      <div className="footer-trust"><span><ShieldCheck /> LMS-ready identity</span><span><Radio /> Live learning</span><span><BookHeart /> 13 curriculum areas</span></div>
      <p>Quran text and translations: <a href="https://github.com/risan/quran-json" target="_blank" rel="noreferrer">quran-json</a>, CC BY 4.0. Audio: <a href="https://alquran.cloud/api" target="_blank" rel="noreferrer">Islamic Network</a>. Word by word: meanings and word audio from <a href="https://quran.com" target="_blank" rel="noreferrer">Quran.com</a>; grammar from the <a href="https://github.com/NoorBayan/Quranic" target="_blank" rel="noreferrer">Quranic Treebank</a> (MIT) and the <a href="https://corpus.quran.com" target="_blank" rel="noreferrer">Quranic Arabic Corpus</a> (GNU GPL). Verify recitation with a qualified teacher.</p>
    </footer>
  </div>;
}
